<template>
  <div
    class="absolute left-0.5 right-0.5 rounded overflow-hidden cursor-pointer group select-none"
    :style="cardStyle"
    @click.stop="emit('click', booking, $event.currentTarget.getBoundingClientRect())"
  >
    <div class="px-1.5 py-0.5 h-full overflow-hidden" :style="textStyle">
      <p class="text-[12px] leading-tight flex items-center gap-1 min-w-0">
        <svg v-if="booking.internal_notes" class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="Has private notes">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        <span class="truncate">{{ booking.title || booking.name }}</span>
      </p>
      <p v-if="tall" class="text-[12px] leading-tight truncate">
        {{ timeLabel }}
      </p>
      <p v-if="taller && mainLocation" class="text-[12px] leading-tight truncate">
        {{ mainLocation }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useGlobalStore }           from 'stores/global.js'
import { useCalendarSettingsStore } from 'stores/calendarSettings.js'

const globalStore   = useGlobalStore()
const settingsStore = useCalendarSettingsStore()

const ROW_HEIGHT = 64 // px per hour

const props = defineProps({
  booking:    { type: Object,  required: true },
  dayStart:   { type: Date,    required: true }, // midnight of the column's day
})

const emit = defineEmits(['click'])

function minsInTz(dateStr, tz) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour: 'numeric', minute: 'numeric', hour12: false,
    }).formatToParts(new Date(dateStr))
    const h = parseInt(parts.find(p => p.type === 'hour').value) % 24
    const m = parseInt(parts.find(p => p.type === 'minute').value)
    return h * 60 + m
  } catch {
    const d = new Date(dateStr); return d.getHours() * 60 + d.getMinutes()
  }
}

const top = computed(() => {
  const tz   = settingsStore.date_primary_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  const mins = minsInTz(props.booking.start_at ?? props.booking.start, tz)
  return (mins / 60) * ROW_HEIGHT
})

const height = computed(() => {
  const start = new Date(props.booking.start_at ?? props.booking.start)
  const end   = new Date(props.booking.end_at   ?? props.booking.end)
  const mins  = (end - start) / 60000
  return Math.max((mins / 60) * ROW_HEIGHT, 18)
})

const tall   = computed(() => height.value >= 36)
const taller = computed(() => height.value >= 52) // room for a third line

const mainLocation = computed(() => {
  const loc = props.booking.location
  if (!loc || typeof loc !== 'string') return ''
  return loc.split(',')[0].trim()
})

// color_code = calendar's color (connected cal) or booking's own color (timeldo)
const color = computed(() => props.booking.color_code || '#3b82f6')

const isPast = computed(() => {
  const end = new Date(props.booking.end_at ?? props.booking.end)
  return end < new Date()
})

// The owner's own RSVP is "maybe" — hatch the chip like Google Calendar
const isTentative = computed(() => props.booking.calendar_owner_status === 'tentative')

// Declined: outlined chip, colored struck-through text, no fill
const isDeclined = computed(() => props.booking.calendar_owner_status === 'declined')

const textStyle = computed(() =>
  isDeclined.value
    ? { color: color.value, textDecoration: 'line-through' }
    : { color: '#ffffff' }
)

function darkenColor(hex, factor = 0.3) {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  const n = parseInt(full, 16)
  const r = Math.round(((n >> 16) & 0xff) * (1 - factor))
  const g = Math.round(((n >> 8)  & 0xff) * (1 - factor))
  const b = Math.round(( n        & 0xff) * (1 - factor))
  return `rgb(${r},${g},${b})`
}

const cardStyle = computed(() => {
  const style = {
    top:     `${top.value}px`,
    height:  `${height.value}px`,
    opacity: settingsStore.reduce_brightness_of_past_bookings && isPast.value ? '0.45' : '1',
  }
  if (isDeclined.value) {
    style.backgroundColor = 'white'
    style.border = `1.5px solid ${color.value}`
  } else if (isTentative.value) {
    // Thin darker lines of the same color over the solid base
    const dark = darkenColor(color.value)
    style.backgroundImage =
      `repeating-linear-gradient(45deg, ${color.value} 0px, ${color.value} 8px, ${dark} 8px, ${dark} 10px)`
  } else {
    style.backgroundColor = color.value
  }
  return style
})

const timeLabel = computed(() => {
  const tz    = settingsStore.date_primary_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  const is12h = globalStore.user?.time_format !== '24_hours'
  const fmt   = (d) => new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: is12h, timeZone: tz })
  return `${fmt(props.booking.start_at ?? props.booking.start)} – ${fmt(props.booking.end_at ?? props.booking.end)}`
})
</script>
